import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { examsService, examSchedulesService, type ExamSchedule, type ExamPaper, type PaperRosterEntry, type ReportCard, type RubricComponent } from "@/lib/services";
import { useAuth } from "@/lib/auth";
import { byRollNo } from "@/lib/utils";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { CalendarDays, CheckCircle2, Download, FileSpreadsheet, FileText, GraduationCap, ListChecks, Loader2, Lock, MessageSquare, Pencil, Plus, Printer, Save, Send, Sparkles, Trash2, Unlock, Upload, X } from "lucide-react";
import { toast } from "sonner";

const CYCLE_STATUS_VARIANT: Record<string, any> = {
  DRAFT: "secondary", READY_FOR_REVIEW: "warning", APPROVED: "info", PUBLISHED: "success", LOCKED: "destructive",
};

export function ExamsPage() {
  const { user } = useAuth();
  const isAdminTeacher = user?.role === "admin" || user?.role === "teacher" || user?.role === "examhead";

  const { data: cycles = [], isPending: cLoading } = useQuery({
    queryKey: ["exams", "cycles"], queryFn: () => examsService.cycles(), enabled: isAdminTeacher,
  });
  const { data: myCards = [], isPending: rcLoading } = useQuery({
    queryKey: ["exams", "myCards"], queryFn: () => examsService.myReportCards(),
    enabled: !isAdminTeacher,
  });
  const [activeCycle, setActiveCycle] = useState<string | null>(null);
  const [activeRC, setActiveRC] = useState<ReportCard | null>(null);
  const [creatingCycle, setCreatingCycle] = useState(false);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Exams</h1>
          <p className="mt-1 text-sm text-muted-foreground">{isAdminTeacher ? `${cycles.length} cycle(s)` : `${myCards.length} report card(s)`}</p>
        </div>
        {isAdminTeacher && (
          <Button size="sm" onClick={() => setCreatingCycle(true)}>
            <Plus className="mr-2 h-4 w-4" /> New cycle
          </Button>
        )}
      </div>

      <ExaminationScheduleSection />

      {isAdminTeacher ? (
        <Card><CardContent className="p-0">
          {cLoading ? <div className="flex h-24 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div> :
           cycles.length === 0 ? (
             <div className="p-8 text-center text-sm text-muted-foreground">
               No exam cycles yet. Click <strong>New cycle</strong> to create one.
             </div>
           ) : (
            <Table>
              <TableHeader><TableRow>
                <TableHead>Name</TableHead><TableHead>Type</TableHead><TableHead>Term</TableHead>
                <TableHead>Schedule</TableHead><TableHead>Status</TableHead><TableHead className="w-24" />
              </TableRow></TableHeader>
              <TableBody>
                {cycles.map((c) => (
                  <TableRow key={c.id} className="cursor-pointer" onClick={() => setActiveCycle(c.id)}>
                    <TableCell className="font-semibold"><div className="flex items-center gap-2"><GraduationCap className="h-4 w-4 text-primary" />{c.name}</div></TableCell>
                    <TableCell><Badge variant="outline">{c.type.replace("_", " ")}</Badge></TableCell>
                    <TableCell className="text-muted-foreground">{c.term || "—"}</TableCell>
                    <TableCell className="text-muted-foreground">{c.starts_on} → {c.ends_on}</TableCell>
                    <TableCell><Badge variant={CYCLE_STATUS_VARIANT[c.result_status] || "secondary"}>{c.result_status.replace("_", " ")}</Badge></TableCell>
                    <TableCell><Button size="sm" variant="ghost">Open</Button></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>)}
        </CardContent></Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {rcLoading ? <Loader2 className="h-6 w-6 animate-spin text-primary" /> : myCards.map((rc) => (
            <Card key={rc.id} className="cursor-pointer hover:bg-accent" onClick={() => setActiveRC(rc)}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">{rc.cycle_name}</CardTitle>
                  <Badge variant="success">{rc.grade_overall}</Badge>
                </div>
                <CardDescription>{rc.student_name} · {rc.class_name}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-4">
                  <div className="text-3xl font-bold tabular-nums text-emerald-700">{rc.pct.toFixed(1)}%</div>
                  <div className="text-sm text-muted-foreground">
                    <div>{rc.total_obtained} / {rc.total_max}</div>
                    {rc.rank_in_section != null && <div>Rank #{rc.rank_in_section}</div>}
                    {rc.gpa != null && <div>GPA {rc.gpa.toFixed(2)}</div>}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {activeCycle && <CycleDialog id={activeCycle} onClose={() => setActiveCycle(null)} />}
      {activeRC && <ReportCardDialog rc={activeRC} onClose={() => setActiveRC(null)} />}
      {creatingCycle && <CreateCycleDialog onClose={() => setCreatingCycle(false)} />}
    </div>
  );
}

function CycleDialog({ id, onClose }: { id: string; onClose: () => void }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const { data, isPending, refetch } = useQuery({ queryKey: ["exams", "cycle", id], queryFn: () => examsService.cycle(id) });
  const { data: cards = [], refetch: refetchCards } = useQuery({
    queryKey: ["exams", "cycle", id, "cards"],
    queryFn: () => examsService.reportCards({ cycle_id: id }),
    enabled: data?.cycle.result_status === "PUBLISHED",
  });
  const pub = useMutation({
    mutationFn: () => examsService.publish(id),
    onSuccess: (r) => { toast.success(`Published ${r.published} report card(s)`); qc.invalidateQueries({ queryKey: ["exams"] }); refetch(); refetchCards(); },
    onError: (e: any) => toast.error(e.message),
  });
  const unpub = useMutation({
    mutationFn: () => examsService.unpublish(id),
    onSuccess: () => { toast.info("Cycle unpublished"); qc.invalidateQueries({ queryKey: ["exams"] }); refetch(); },
    onError: (e: any) => toast.error(e.message),
  });

  const [activePaper, setActivePaper] = useState<ExamPaper | null>(null);
  const [rubricPaper, setRubricPaper] = useState<ExamPaper | null>(null);
  const [remarksRC, setRemarksRC] = useState<ReportCard | null>(null);

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{data?.cycle.name || "Cycle"}</DialogTitle>
          <DialogDescription>{data ? `${data.cycle.starts_on} → ${data.cycle.ends_on} · ${data.papers.length} paper(s)` : ""}</DialogDescription>
        </DialogHeader>
        {isPending || !data ? <Loader2 className="h-6 w-6 animate-spin" /> : (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Badge variant={CYCLE_STATUS_VARIANT[data.cycle.result_status] || "secondary"}>{data.cycle.result_status}</Badge>
              <span className="text-sm text-muted-foreground">{data.papers.length} papers</span>
            </div>
            <div className="rounded-md border">
              <table className="w-full text-sm">
                <thead className="bg-muted/60">
                  <tr className="text-left">
                    <th className="p-2 font-medium">Subject</th>
                    <th className="p-2 font-medium">Class</th>
                    <th className="p-2 font-medium">Date</th>
                    <th className="p-2 font-medium">Rubrics</th>
                    <th className="p-2 font-medium text-right">Total</th>
                    <th className="p-2 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {data.papers.map((p) => (
                    <tr key={p.id} className="border-t align-middle">
                      <td className="p-2 font-semibold">{p.subject}</td>
                      <td className="p-2 text-muted-foreground">{p.class_name}</td>
                      <td className="p-2 text-muted-foreground">{p.scheduled_on}</td>
                      <td className="p-2 text-xs">
                        {(p.rubrics && p.rubrics.length) ? (
                          <div className="flex flex-wrap gap-1">
                            {p.rubrics.map((r) => (
                              <span key={r.name} className="rounded bg-primary/10 px-1.5 py-0.5 text-primary">
                                {r.name} <span className="text-muted-foreground">·{r.max_marks}</span>
                              </span>
                            ))}
                          </div>
                        ) : <span className="text-muted-foreground italic">not set</span>}
                      </td>
                      <td className="p-2 text-right tabular-nums">{p.total_marks}</td>
                      <td className="p-2 text-right">
                        <div className="flex justify-end gap-1">
                          <Button size="sm" variant="ghost" onClick={() => setRubricPaper(p)} title="Set rubrics">
                            <ListChecks className="h-3.5 w-3.5" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setActivePaper(p)} title="Enter marks">
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex flex-wrap gap-2">
              {data.cycle.result_status !== "PUBLISHED" ? (
                <Button onClick={() => pub.mutate()} disabled={pub.isPending}>
                  {pub.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />}
                  Publish results
                </Button>
              ) : (
                <Button variant="outline" onClick={() => unpub.mutate()} disabled={unpub.isPending}>Unpublish</Button>
              )}
              {(user?.role === "admin" || user?.role === "principal") && (
                <DeleteCycleButton
                  cycleId={id} cycleName={data.cycle.name}
                  isPublished={data.cycle.result_status === "PUBLISHED"}
                  papersCount={data.papers.length} cardsCount={cards.length}
                  onDeleted={onClose}
                />
              )}
            </div>

            {data.cycle.result_status === "PUBLISHED" && cards.length > 0 && (
              <div>
                <div className="mb-2 mt-4 flex flex-wrap items-center justify-between gap-2">
                  <h4 className="flex items-center gap-2 text-sm font-semibold">
                    <FileText className="h-4 w-4 text-primary" /> Report Cards ({cards.length})
                    <span className="text-xs font-normal text-muted-foreground">
                      · {cards.filter((c) => c.released_to_parents).length} released to parents
                    </span>
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    <BulkPrintButton cycleId={id} cycleName={data.cycle.name} cards={cards} />
                    <ReleaseAllButtons cycleId={id} refetchCards={refetchCards} />
                  </div>
                </div>
                <RemarksBulkButtons cycleId={id} cycleName={data.cycle.name} cards={cards} refetchCards={refetchCards} />
                <div className="rounded-md border">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/60">
                      <tr className="text-left">
                        <th className="p-2 font-medium">Student</th>
                        <th className="p-2 font-medium">Class</th>
                        <th className="p-2 font-medium text-right">Total</th>
                        <th className="p-2 font-medium text-right">%</th>
                        <th className="p-2 font-medium">Grade</th>
                        <th className="p-2 font-medium text-right">Rank</th>
                        <th className="p-2 font-medium">Released</th>
                        <th className="p-2 font-medium text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cards.map((rc) => (
                        <tr key={rc.id} className="border-t">
                          <td className="p-2 font-semibold">{rc.student_name}</td>
                          <td className="p-2 text-muted-foreground">{rc.class_name}</td>
                          <td className="p-2 text-right tabular-nums">{rc.total_obtained} / {rc.total_max}</td>
                          <td className="p-2 text-right tabular-nums font-semibold text-emerald-700">{rc.pct.toFixed(1)}%</td>
                          <td className="p-2"><Badge variant="outline">{rc.grade_overall}</Badge></td>
                          <td className="p-2 text-right">{rc.rank_in_section ? `#${rc.rank_in_section}` : "—"}</td>
                          <td className="p-2">
                            {rc.released_to_parents
                              ? <Badge variant="success">Released</Badge>
                              : <Badge variant="secondary">Draft</Badge>}
                          </td>
                          <td className="p-2 text-right">
                            <div className="flex justify-end gap-1">
                              <ReleaseCardButton rc={rc} refetchCards={refetchCards} />
                              <Button size="sm" variant="ghost" onClick={() => setRemarksRC(rc)} title="Edit remarks">
                                <MessageSquare className="h-3.5 w-3.5" />
                              </Button>
                              <DownloadPdfButton rc={rc} />
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
      {activePaper && <MarksEntryDialog paper={activePaper} onClose={() => setActivePaper(null)} />}
      {rubricPaper && <RubricEditorDialog paper={rubricPaper} onClose={() => { setRubricPaper(null); refetch(); }} />}
      {remarksRC && <RemarksEditorDialog rc={remarksRC} onClose={() => { setRemarksRC(null); refetchCards(); }} />}
    </Dialog>
  );
}

function DownloadPdfButton({ rc }: { rc: ReportCard }) {
  const [loading, setLoading] = useState(false);
  const handleDownload = async () => {
    setLoading(true);
    try {
      const blob = await examsService.fetchReportCardPdf(rc.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ReportCard_${rc.student_name.replace(/\s+/g, "_")}_${rc.cycle_name.replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 3000);
    } catch (e: any) {
      toast.error(e.message || "Download failed");
    } finally {
      setLoading(false);
    }
  };
  return (
    <Button size="sm" variant="ghost" onClick={handleDownload} disabled={loading} title="Download A4 PDF">
      {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5 text-primary" />}
    </Button>
  );
}

function ReleaseCardButton({ rc, refetchCards }: { rc: ReportCard; refetchCards: () => void }) {
  const mut = useMutation({
    mutationFn: () => rc.released_to_parents ? examsService.unreleaseCard(rc.id) : examsService.releaseCard(rc.id),
    onSuccess: (updated) => {
      toast.success(updated.released_to_parents ? "Released to parents" : "Un-released");
      refetchCards();
    },
    onError: (e: any) => toast.error(e.message),
  });
  return (
    <Button size="sm" variant="ghost" onClick={() => mut.mutate()} disabled={mut.isPending}
            title={rc.released_to_parents ? "Un-release from parents" : "Release to parents"}>
      {mut.isPending
        ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
        : rc.released_to_parents
          ? <Lock className="h-3.5 w-3.5 text-amber-600" />
          : <Send className="h-3.5 w-3.5 text-emerald-600" />}
    </Button>
  );
}

function DeleteCycleButton({
  cycleId, cycleName, isPublished, papersCount, cardsCount, onDeleted,
}: {
  cycleId: string; cycleName: string; isPublished: boolean;
  papersCount: number; cardsCount: number; onDeleted: () => void;
}) {
  const qc = useQueryClient();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [typed, setTyped] = useState("");

  const del = useMutation({
    mutationFn: () => examsService.deleteCycle(cycleId, isPublished),
    onSuccess: (r) => {
      toast.success(
        `Deleted "${cycleName}" · ${r.deleted.papers} papers · ${r.deleted.marks} marks · ${r.deleted.report_cards} report cards`,
      );
      qc.invalidateQueries({ queryKey: ["exams"] });
      setConfirmOpen(false);
      onDeleted();
    },
    onError: (e: any) => toast.error(e.message),
  });

  const matches = typed.trim().toLowerCase() === cycleName.trim().toLowerCase();

  return (
    <>
      <Button variant="destructive" onClick={() => setConfirmOpen(true)}
              className="ml-auto" title="Delete this cycle">
        <Trash2 className="mr-2 h-4 w-4" /> Delete cycle
      </Button>
      {confirmOpen && (
        <Dialog open onOpenChange={(o) => { if (!o) setConfirmOpen(false); }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="text-destructive">Delete exam cycle?</DialogTitle>
              <DialogDescription>
                This permanently removes <strong>{cycleName}</strong> along with:
                <ul className="mt-2 list-disc pl-5 text-sm">
                  <li>{papersCount} paper(s)</li>
                  <li>all recorded marks for this cycle</li>
                  <li>{cardsCount} generated report card(s)</li>
                </ul>
                {isPublished && (
                  <p className="mt-2 text-sm font-medium text-amber-700">
                    This cycle is currently PUBLISHED — report cards visible/released to parents will also be revoked.
                  </p>
                )}
                <p className="mt-3 text-sm">This action cannot be undone.</p>
              </DialogDescription>
            </DialogHeader>
            <div>
              <Label>Type the cycle name to confirm</Label>
              <Input value={typed} onChange={(e) => setTyped(e.target.value)}
                     placeholder={cycleName} autoFocus />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setConfirmOpen(false)}>Cancel</Button>
              <Button variant="destructive" disabled={!matches || del.isPending}
                      onClick={() => del.mutate()}>
                {del.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
                Delete permanently
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}

function BulkPrintButton({ cycleId, cycleName, cards }: {
  cycleId: string; cycleName: string; cards: ReportCard[];
}) {
  const classes = Array.from(new Set(cards.map((c) => c.class_name))).sort();
  const [className, setClassName] = useState<string>("");
  const [onlyReleased, setOnlyReleased] = useState(false);
  const [loading, setLoading] = useState(false);

  const handlePrint = async () => {
    setLoading(true);
    try {
      const blob = await examsService.fetchBulkReportCardsPdf(cycleId, {
        class_name: className || undefined,
        only_released: onlyReleased || undefined,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const cn = (className || "AllClasses").replace(/\s+/g, "_");
      a.href = url;
      a.download = `ReportCards_${cycleName.replace(/\s+/g, "_")}_${cn}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 3000);
      toast.success("Bulk PDF downloaded");
    } catch (e: any) {
      toast.error(e.message || "Bulk print failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center gap-2">
      <select value={className} onChange={(e) => setClassName(e.target.value)}
              className="h-9 rounded-md border border-input bg-background px-2 text-sm">
        <option value="">All classes ({cards.length})</option>
        {classes.map((cn) => (
          <option key={cn} value={cn}>
            {cn} ({cards.filter((c) => c.class_name === cn).length})
          </option>
        ))}
      </select>
      <label className="flex items-center gap-1 text-xs text-muted-foreground">
        <input type="checkbox" checked={onlyReleased} onChange={(e) => setOnlyReleased(e.target.checked)} />
        released only
      </label>
      <Button size="sm" variant="outline" onClick={handlePrint} disabled={loading}>
        {loading ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : <Printer className="mr-2 h-3.5 w-3.5" />}
        Print all
      </Button>
    </div>
  );
}

function ReleaseAllButtons({ cycleId, refetchCards }: { cycleId: string; refetchCards: () => void }) {
  const releaseAll = useMutation({
    mutationFn: () => examsService.releaseAll(cycleId),
    onSuccess: (r) => { toast.success(`Released ${r.released} report card(s) to parents`); refetchCards(); },
    onError: (e: any) => toast.error(e.message),
  });
  const unreleaseAll = useMutation({
    mutationFn: () => examsService.unreleaseAll(cycleId),
    onSuccess: (r) => { toast.info(`Un-released ${r.unreleased} report card(s)`); refetchCards(); },
    onError: (e: any) => toast.error(e.message),
  });
  return (
    <div className="flex gap-2">
      <Button size="sm" variant="outline" onClick={() => releaseAll.mutate()} disabled={releaseAll.isPending}>
        {releaseAll.isPending ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : <Send className="mr-2 h-3.5 w-3.5" />}
        Release all
      </Button>
      <Button size="sm" variant="ghost" onClick={() => unreleaseAll.mutate()} disabled={unreleaseAll.isPending} title="Un-release all">
        {unreleaseAll.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Unlock className="h-3.5 w-3.5" />}
      </Button>
    </div>
  );
}

function RemarksBulkButtons({ cycleId, cycleName, cards, refetchCards }: {
  cycleId: string; cycleName: string; cards: ReportCard[]; refetchCards: () => void;
}) {
  const classes = useMemo(
    () => Array.from(new Set(cards.map((c) => c.class_name).filter(Boolean)))
      .sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" })),
    [cards],
  );
  const [selectedClass, setSelectedClass] = useState<string>(classes.length === 1 ? classes[0] : "");
  const [importing, setImporting] = useState(false);
  const [summary, setSummary] = useState<{ upserted: number; total_rows: number } | null>(null);
  const [skip, setSkip] = useState<{ row: number; gr_no: string; name: string; reason: string }[] | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const handleExport = async () => {
    try {
      const blob = await examsService.fetchCycleRemarksExcel(cycleId, selectedClass || undefined);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Remarks_${cycleName}_${selectedClass || "AllClasses"}.xlsx`.replace(/\s+/g, "_");
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Remarks Excel downloaded");
    } catch (e: any) {
      toast.error(e.message || "Export failed");
    }
  };

  const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setImporting(true); setSkip(null); setSummary(null);
    try {
      const r = await examsService.importCycleRemarksExcel(cycleId, f, selectedClass || undefined);
      setSummary({ upserted: r.upserted, total_rows: r.total_rows });
      setSkip(r.skipped);
      refetchCards();
      if (r.skipped.length === 0) toast.success(`Updated remarks for ${r.upserted} student(s)`);
      else toast.warning(`Updated ${r.upserted} · ${r.skipped.length} skipped`, { duration: 8000 });
    } catch (e: any) {
      toast.error(e.message || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="mb-3 rounded-md border bg-muted/20 p-3" data-testid="remarks-bulk-panel">
      <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden"
        data-testid="remarks-import-file-input" onChange={handleImport} />
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-semibold">Bulk Remarks:</span>
        {classes.length > 1 && (
          <select
            data-testid="remarks-class-filter"
            className="h-8 rounded-md border border-input bg-background px-2 text-sm"
            value={selectedClass}
            onChange={(e) => setSelectedClass(e.target.value)}
          >
            <option value="">All classes</option>
            {classes.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        )}
        <Button size="sm" variant="outline" onClick={handleExport} data-testid="remarks-export-btn">
          <Download className="mr-2 h-3.5 w-3.5" /> Export Remarks
        </Button>
        <Button size="sm" variant="outline" disabled={importing}
          onClick={() => fileRef.current?.click()} data-testid="remarks-import-btn">
          {importing ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : <Upload className="mr-2 h-3.5 w-3.5" />}
          Import Remarks
        </Button>
        <span className="text-xs text-muted-foreground">Matched by <b>GR No.</b> · updates Class Teacher, Principal, Conduct &amp; Discipline</span>
      </div>
      {summary && (
        <div
          className={`mt-2 rounded-md border p-2 text-sm ${
            (skip?.length ?? 0) > 0
              ? "border-yellow-500/40 bg-yellow-50 text-yellow-900"
              : "border-green-500/40 bg-green-50 text-green-900"
          }`}
          data-testid="remarks-import-summary"
        >
          <div className="flex items-center justify-between gap-2">
            <span className="font-semibold">
              Import complete — {summary.upserted} of {summary.total_rows} row(s) updated
              {(skip?.length ?? 0) > 0 ? ` · ${skip!.length} skipped` : ""}
            </span>
            <Button size="sm" variant="ghost" onClick={() => { setSummary(null); setSkip(null); }} data-testid="remarks-import-summary-dismiss">
              <X className="h-4 w-4" />
            </Button>
          </div>
          {(skip?.length ?? 0) > 0 && (
            <div className="mt-2 max-h-40 overflow-y-auto rounded border border-yellow-500/30 bg-white/60" data-testid="remarks-skip-list">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-yellow-100 text-yellow-900">
                  <tr className="text-left">
                    <th className="p-2 w-14">Row</th>
                    <th className="p-2 w-24">GR No.</th>
                    <th className="p-2">Name</th>
                    <th className="p-2">Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {skip!.map((s, i) => (
                    <tr key={i} className="border-t border-yellow-500/20" data-testid={`remarks-skip-row-${i}`}>
                      <td className="p-2 tabular-nums">{s.row}</td>
                      <td className="p-2 tabular-nums">{s.gr_no || "—"}</td>
                      <td className="p-2">{s.name || "—"}</td>
                      <td className="p-2">{s.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function RubricEditorDialog({ paper, onClose }: { paper: ExamPaper; onClose: () => void }) {
  const qc = useQueryClient();
  const initial: RubricComponent[] = paper.rubrics && paper.rubrics.length > 0
    ? paper.rubrics.map((r) => ({ ...r }))
    : [
        { name: "Written", max_marks: 80, weightage_pct: 100 },
        { name: "Oral",    max_marks: 10, weightage_pct: 100 },
        { name: "Project", max_marks: 10, weightage_pct: 100 },
      ];
  const [rows, setRows] = useState<RubricComponent[]>(initial);

  const total = rows.reduce((s, r) => s + (Number(r.max_marks) || 0), 0);

  const save = useMutation({
    mutationFn: () => examsService.setRubrics(paper.id, rows.map((r) => ({
      name: r.name.trim(), max_marks: Number(r.max_marks) || 0,
      weightage_pct: Number(r.weightage_pct ?? 100),
    })).filter((r) => r.name && r.max_marks > 0)),
    onSuccess: () => {
      toast.success(`Rubrics saved · Total ${total} marks`);
      qc.invalidateQueries({ queryKey: ["exams"] });
      onClose();
    },
    onError: (e: any) => toast.error(e.message),
  });

  const canSave = rows.length > 0 && rows.every((r) => r.name.trim() && Number(r.max_marks) > 0);

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ListChecks className="h-5 w-5 text-primary" /> Rubrics — {paper.subject}
          </DialogTitle>
          <DialogDescription>
            {paper.class_name} · Define components (e.g. Written 80, Oral 10, Project 10). Total will become the paper&apos;s max marks.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {rows.map((r, i) => (
            <div key={i} className="flex items-center gap-2">
              <Input value={r.name} placeholder="Component name (e.g. Written)"
                onChange={(e) => setRows((rs) => rs.map((x, idx) => idx === i ? { ...x, name: e.target.value } : x))}
                className="flex-1" />
              <Input type="number" min={0} max={200} value={r.max_marks}
                onChange={(e) => setRows((rs) => rs.map((x, idx) => idx === i ? { ...x, max_marks: Number(e.target.value) } : x))}
                className="w-24" placeholder="Max" />
              <Button size="icon" variant="ghost" onClick={() => setRows((rs) => rs.filter((_, idx) => idx !== i))}>
                <X className="h-4 w-4 text-destructive" />
              </Button>
            </div>
          ))}
          <Button size="sm" variant="outline" onClick={() => setRows((rs) => [...rs, { name: "", max_marks: 10, weightage_pct: 100 }])}>
            <Plus className="mr-2 h-4 w-4" /> Add component
          </Button>
          <div className="mt-2 rounded-md border bg-slate-50 p-3 text-sm">
            <span className="text-muted-foreground">Paper total: </span>
            <span className="text-lg font-bold tabular-nums text-primary">{total}</span>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => save.mutate()} disabled={!canSave || save.isPending}>
            {save.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
            Save rubrics
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MarksEntryDialog({ paper, onClose }: { paper: ExamPaper; onClose: () => void }) {
  const qc = useQueryClient();
  const { data, isPending } = useQuery({
    queryKey: ["exams", "paperMarks", paper.id],
    queryFn: () => examsService.paperMarks(paper.id),
  });
  const rubrics = data?.paper.rubrics ?? paper.rubrics ?? [];
  const [rows, setRows] = useState<Record<string, PaperRosterEntry>>({});
  const [importing, setImporting] = useState(false);
  const [importSummary, setImportSummary] = useState<{ upserted: number; total_rows: number } | null>(null);
  const [skipReport, setSkipReport] = useState<{ row: number; gr_no: string; name: string; reason: string }[] | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleExport = async () => {
    try {
      const blob = await examsService.fetchMarksExcel(paper.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Marks_${paper.class_name}_${paper.subject}.xlsx`.replace(/\s+/g, "_");
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Excel downloaded");
    } catch (e: any) {
      toast.error(e.message || "Export failed");
    }
  };

  const handleImportFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setImporting(true);
    setSkipReport(null);
    setImportSummary(null);
    try {
      const r = await examsService.importMarksExcel(paper.id, f);
      setImportSummary({ upserted: r.upserted, total_rows: r.total_rows });
      setSkipReport(r.skipped);
      qc.invalidateQueries({ queryKey: ["exams", "paperMarks", paper.id] });
      qc.invalidateQueries({ queryKey: ["exams"] });
      if (r.skipped.length === 0) {
        toast.success(`Imported ${r.upserted} row(s) successfully`);
      } else {
        toast.warning(`Imported ${r.upserted} row(s) · ${r.skipped.length} skipped`, { duration: 8000 });
      }
    } catch (e: any) {
      toast.error(e.message || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  // Seed rows from roster on load
  useEffect(() => {
    if (data?.roster) {
      const map: Record<string, PaperRosterEntry> = {};
      data.roster.forEach((r) => { map[r.student_id] = { ...r, component_marks: r.component_marks || {} }; });
      setRows(map);
    }
  }, [data?.roster]);

  const setComponent = (studentId: string, cname: string, value: string) => {
    setRows((prev) => {
      const cur = prev[studentId];
      const next = { ...cur, component_marks: { ...(cur.component_marks || {}), [cname]: value === "" ? undefined : Number(value) } };
      return { ...prev, [studentId]: next };
    });
  };
  const setTotal = (studentId: string, value: string) => {
    setRows((prev) => ({ ...prev, [studentId]: { ...prev[studentId], marks_obtained: value === "" ? null : Number(value) } }));
  };
  const setAbsent = (studentId: string, v: boolean) => {
    setRows((prev) => ({ ...prev, [studentId]: { ...prev[studentId], is_absent: v, is_exempted: v ? false : prev[studentId].is_exempted } }));
  };

  // Ordered rows exactly as rendered — used to map a pasted column onto rows.
  const orderedRows = () =>
    Object.values(rows).sort(byRollNo);

  // Single-column paste: copy a column of marks from Excel and paste into a cell
  // to fill downward from that row. `kind` is "component" (with cname) or "total".
  const handlePaste = (
    e: React.ClipboardEvent<HTMLInputElement>,
    studentId: string,
    kind: "component" | "total",
    cname?: string,
  ) => {
    const text = e.clipboardData.getData("text");
    const parts = text
      .split(/\r\n|\r|\n/)
      .map((s) => s.split("\t")[0].trim());
    while (parts.length && parts[parts.length - 1] === "") parts.pop();
    if (parts.length <= 1) return; // single value → let the browser paste normally
    e.preventDefault();
    const list = orderedRows();
    const startIdx = list.findIndex((r) => r.student_id === studentId);
    if (startIdx < 0) return;
    setRows((prev) => {
      const next = { ...prev };
      let filled = 0;
      parts.forEach((val, i) => {
        const target = list[startIdx + i];
        if (!target) return;
        const cur = next[target.student_id];
        if (!cur || cur.is_absent) return;
        if (kind === "component" && cname) {
          next[target.student_id] = {
            ...cur,
            component_marks: { ...(cur.component_marks || {}), [cname]: val === "" ? undefined : Number(val) },
          };
        } else {
          next[target.student_id] = { ...cur, marks_obtained: val === "" ? null : Number(val) };
        }
        filled += 1;
      });
      toast.success(`Pasted ${filled} value(s)`);
      return next;
    });
  };

  const rowTotal = (r: PaperRosterEntry): number | null => {
    if (r.is_absent || r.is_exempted) return null;
    if (rubrics.length > 0) {
      const cm = r.component_marks || {};
      const vals = rubrics.map((rb) => Number(cm[rb.name] ?? 0));
      if (vals.every((v) => !cm[rubrics[0].name] && Object.keys(cm).length === 0)) return null;
      return vals.reduce((a, b) => a + (isNaN(b) ? 0 : b), 0);
    }
    return r.marks_obtained;
  };

  const save = useMutation({
    mutationFn: () => {
      const records = Object.values(rows).map((r) => {
        const rec: any = { student_id: r.student_id, is_absent: r.is_absent, is_exempted: r.is_exempted, teacher_remarks: r.teacher_remarks };
        if (rubrics.length > 0) {
          // build component_marks, skip undefined
          const cm: Record<string, number> = {};
          Object.entries(r.component_marks || {}).forEach(([k, v]) => {
            if (v !== undefined && v !== null && !isNaN(Number(v))) cm[k] = Number(v);
          });
          if (Object.keys(cm).length > 0) rec.component_marks = cm;
          else rec.marks_obtained = null;
        } else {
          rec.marks_obtained = r.marks_obtained ?? null;
        }
        return rec;
      });
      return examsService.saveMarks(paper.id, records);
    },
    onSuccess: (r) => {
      const skipped = r.skipped ?? [];
      if (skipped.length > 0) {
        const names = skipped.map((s) => rows[s.student_id]?.student_name || s.student_id);
        toast.warning(
          `Saved ${r.upserted}. ${skipped.length} not saved: ${names.slice(0, 5).join(", ")}${names.length > 5 ? "…" : ""}`,
          { duration: 8000 },
        );
        qc.invalidateQueries({ queryKey: ["exams"] });
        // keep dialog open so the user can see which rows were skipped
      } else {
        toast.success(`Saved marks for ${r.upserted} student(s)`);
        qc.invalidateQueries({ queryKey: ["exams"] });
        onClose();
      }
    },
    onError: (e: any) => toast.error(e.message),
  });

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pencil className="h-5 w-5 text-primary" /> Marks Entry — {paper.subject} · {paper.class_name}
          </DialogTitle>
          <DialogDescription>
            {paper.scheduled_on} · Total {paper.total_marks} marks
            {rubrics.length > 0 && <> · Components: {rubrics.map((r) => `${r.name} (${r.max_marks})`).join(", ")}</>}
            <span className="mt-1 block text-xs text-muted-foreground">
              Tip: copy a column of marks from Excel/Sheets and paste it into the first cell to fill down the rows.
            </span>
          </DialogDescription>
        </DialogHeader>
        {isPending ? <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div> :
        !data ? null :
         (
          <div className="space-y-3">
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              className="hidden"
              data-testid="marks-import-file-input"
              onChange={handleImportFile}
            />
            <div className="flex flex-wrap items-center gap-2">
              <Button size="sm" variant="outline" onClick={handleExport} data-testid="marks-export-excel-btn">
                <Download className="mr-2 h-4 w-4" /> Download Excel
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={importing}
                onClick={() => fileInputRef.current?.click()}
                data-testid="marks-import-excel-btn"
              >
                {importing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
                Import Excel
              </Button>
              <span className="text-xs text-muted-foreground">
                <FileSpreadsheet className="mr-1 inline h-3.5 w-3.5" />
                Rows are matched by <b>GR No.</b>
              </span>
            </div>
            {importSummary && (
              <div
                className={`rounded-md border p-3 text-sm ${
                  (skipReport?.length ?? 0) > 0
                    ? "border-yellow-500/40 bg-yellow-50 text-yellow-900"
                    : "border-green-500/40 bg-green-50 text-green-900"
                }`}
                data-testid="marks-import-summary"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-semibold">
                    Import complete — {importSummary.upserted} of {importSummary.total_rows} row(s) saved
                    {(skipReport?.length ?? 0) > 0 ? ` · ${skipReport!.length} skipped` : ""}
                  </span>
                  <Button size="sm" variant="ghost" onClick={() => { setImportSummary(null); setSkipReport(null); }} data-testid="marks-import-summary-dismiss">
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                {(skipReport?.length ?? 0) > 0 && (
                  <div className="mt-2 max-h-40 overflow-y-auto rounded border border-yellow-500/30 bg-white/60" data-testid="marks-skip-list">
                    <table className="w-full text-xs">
                      <thead className="sticky top-0 bg-yellow-100 text-yellow-900">
                        <tr className="text-left">
                          <th className="p-2 w-14">Row</th>
                          <th className="p-2 w-24">GR No.</th>
                          <th className="p-2">Name</th>
                          <th className="p-2">Reason</th>
                        </tr>
                      </thead>
                      <tbody>
                        {skipReport!.map((s, i) => (
                          <tr key={i} className="border-t border-yellow-500/20" data-testid={`marks-skip-row-${i}`}>
                            <td className="p-2 tabular-nums">{s.row}</td>
                            <td className="p-2 tabular-nums">{s.gr_no || "—"}</td>
                            <td className="p-2">{s.name || "—"}</td>
                            <td className="p-2">{s.reason}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
            {rubrics.length === 0 && (
              <div className="rounded-md border border-yellow-500/40 bg-yellow-50 p-3 text-sm text-yellow-800">
                No rubrics defined. You can either enter a single total (out of {paper.total_marks}), or set rubrics first via the <ListChecks className="inline h-3.5 w-3.5" /> icon on the previous screen.
              </div>
            )}
            <div className="max-h-[60vh] overflow-y-auto rounded-md border">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-muted/80">
                  <tr className="text-left">
                    <th className="w-16 p-2">Roll</th>
                    <th className="p-2">Student</th>
                    {rubrics.length > 0 ? rubrics.map((r) => (
                      <th key={r.name} className="p-2 text-right w-24">{r.name} <span className="text-xs text-muted-foreground">/{r.max_marks}</span></th>
                    )) : (
                      <th className="p-2 text-right w-24">Marks <span className="text-xs text-muted-foreground">/{paper.total_marks}</span></th>
                    )}
                    <th className="p-2 text-right w-20">Total</th>
                    <th className="p-2 w-20 text-center">Absent</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.values(rows).sort(byRollNo)
                    .map((r) => {
                      const t = rowTotal(r);
                      return (
                        <tr key={r.student_id} className={`border-t ${r.is_absent ? "opacity-50" : ""}`}>
                          <td className="p-2 text-muted-foreground">{r.roll_no}</td>
                          <td className="p-2 font-semibold">{r.student_name}</td>
                          {rubrics.length > 0 ? rubrics.map((rb) => (
                            <td key={rb.name} className="p-1 text-right">
                              <Input type="number" min={0} max={rb.max_marks} disabled={r.is_absent}
                                className="h-8 w-20 text-right"
                                value={((r.component_marks || {})[rb.name] ?? "") as any}
                                onPaste={(e) => handlePaste(e, r.student_id, "component", rb.name)}
                                onChange={(e) => setComponent(r.student_id, rb.name, e.target.value)} />
                            </td>
                          )) : (
                            <td className="p-1 text-right">
                              <Input type="number" min={0} max={paper.total_marks} disabled={r.is_absent}
                                className="h-8 w-20 text-right"
                                value={r.marks_obtained ?? ""}
                                onPaste={(e) => handlePaste(e, r.student_id, "total")}
                                onChange={(e) => setTotal(r.student_id, e.target.value)} />
                            </td>
                          )}
                          <td className="p-2 text-right font-semibold tabular-nums">
                            {t == null ? (r.is_absent ? "AB" : "—") : `${t} / ${paper.total_marks}`}
                          </td>
                          <td className="p-2 text-center">
                            <input type="checkbox" checked={!!r.is_absent}
                              onChange={(e) => setAbsent(r.student_id, e.target.checked)} />
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => save.mutate()} disabled={save.isPending || isPending}>
            {save.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
            Save all marks
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RemarksEditorDialog({ rc, onClose }: { rc: ReportCard; onClose: () => void }) {
  const qc = useQueryClient();
  const [classRemark, setClassRemark] = useState(rc.class_teacher_remark || "");
  const [principalRemark, setPrincipalRemark] = useState(rc.principal_remark || "");
  const [conduct, setConduct] = useState(rc.conduct_remark || "");
  const [discipline, setDiscipline] = useState(rc.discipline_remark || "");
  const [attendance, setAttendance] = useState<string>(rc.attendance_pct != null ? String(rc.attendance_pct) : "");

  const save = useMutation({
    mutationFn: () => examsService.updateRemarks(rc.id, {
      class_teacher_remark: classRemark, principal_remark: principalRemark,
      conduct_remark: conduct, discipline_remark: discipline,
      attendance_pct: attendance ? Number(attendance) : undefined,
    }),
    onSuccess: () => {
      toast.success("Remarks updated");
      qc.invalidateQueries({ queryKey: ["exams"] });
      onClose();
    },
    onError: (e: any) => toast.error(e.message),
  });

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Report Card Remarks — {rc.student_name}</DialogTitle>
          <DialogDescription>{rc.cycle_name} · {rc.class_name}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Class Teacher&apos;s remark</Label>
            <Input value={classRemark} onChange={(e) => setClassRemark(e.target.value)} placeholder="e.g. A dedicated learner…" />
          </div>
          <div>
            <Label>Principal&apos;s remark</Label>
            <Input value={principalRemark} onChange={(e) => setPrincipalRemark(e.target.value)} placeholder="e.g. Keep up the good work…" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Conduct</Label>
              <Input value={conduct} onChange={(e) => setConduct(e.target.value)} placeholder="Polite / Excellent" />
            </div>
            <div>
              <Label>Discipline</Label>
              <Input value={discipline} onChange={(e) => setDiscipline(e.target.value)} placeholder="Punctual / Good" />
            </div>
          </div>
          <div>
            <Label>Attendance %</Label>
            <Input type="number" min={0} max={100} step="0.1" value={attendance}
              onChange={(e) => setAttendance(e.target.value)} placeholder="e.g. 94.5" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => save.mutate()} disabled={save.isPending}>
            {save.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
            Save remarks
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CreateCycleDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [term, setTerm] = useState("Term 1 2026");
  const [type, setType] = useState("MID_TERM");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [className, setClassName] = useState("Grade 5A");
  const [subjectsText, setSubjectsText] = useState("Mathematics, Science, English, Hindi");
  const [totalMarks, setTotalMarks] = useState("100");

  const create = useMutation({
    mutationFn: () => {
      const subjects = subjectsText.split(",").map((s) => s.trim()).filter(Boolean);
      // distribute dates evenly across start..end
      const startDate = new Date(start + "T00:00:00").getTime();
      const endDate = new Date(end + "T00:00:00").getTime();
      const gap = subjects.length > 1 ? (endDate - startDate) / (subjects.length - 1) : 0;
      const papers = subjects.map((sub, i) => ({
        class_name: className,
        subject: sub,
        scheduled_on: new Date(startDate + gap * i).toISOString().slice(0, 10),
        total_marks: Number(totalMarks) || 100,
      }));
      return examsService.createCycle({ name, term, type, starts_on: start, ends_on: end, papers });
    },
    onSuccess: () => {
      toast.success("Cycle created");
      qc.invalidateQueries({ queryKey: ["exams"] });
      onClose();
    },
    onError: (e: any) => toast.error(e.message),
  });

  const canSave = !!name && !!start && !!end && start <= end && !!className && subjectsText.split(",").filter((s) => s.trim()).length > 0;

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>New Exam Cycle</DialogTitle>
          <DialogDescription>Creates a cycle with per-subject papers for one class.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label>Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Final Term 2026" /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Term</Label><Input value={term} onChange={(e) => setTerm(e.target.value)} /></div>
            <div>
              <Label>Type</Label>
              <select value={type} onChange={(e) => setType(e.target.value)} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                <option value="UNIT_TEST">Unit Test</option>
                <option value="FORMATIVE">Formative</option>
                <option value="MID_TERM">Mid-Term</option>
                <option value="FINAL">Final</option>
                <option value="PRACTICAL">Practical</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Start</Label><Input type="date" value={start} onChange={(e) => setStart(e.target.value)} /></div>
            <div><Label>End</Label><Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} /></div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Class</Label><Input value={className} onChange={(e) => setClassName(e.target.value)} placeholder="Grade 5A" /></div>
            <div><Label>Total Marks / paper</Label><Input type="number" value={totalMarks} onChange={(e) => setTotalMarks(e.target.value)} /></div>
          </div>
          <div><Label>Subjects (comma-separated)</Label><Input value={subjectsText} onChange={(e) => setSubjectsText(e.target.value)} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => create.mutate()} disabled={!canSave || create.isPending}>
            {create.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}
            Create cycle
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReportCardDialog({ rc, onClose }: { rc: ReportCard; onClose: () => void }) {
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{rc.student_name}</DialogTitle>
          <DialogDescription>{rc.cycle_name} · {rc.class_name}</DialogDescription>
        </DialogHeader>
        <div className="flex items-center justify-around rounded-md border bg-slate-50 p-4">
          <div className="text-center"><div className="text-4xl font-bold text-emerald-700">{rc.pct.toFixed(1)}%</div><div className="text-xs uppercase text-muted-foreground">Overall</div></div>
          <div className="text-center"><div className="text-2xl font-bold">{rc.grade_overall}</div><div className="text-xs uppercase text-muted-foreground">Grade</div></div>
          {rc.rank_in_section != null && <div className="text-center"><div className="text-2xl font-bold">#{rc.rank_in_section}</div><div className="text-xs uppercase text-muted-foreground">Rank</div></div>}
          {rc.gpa != null && <div className="text-center"><div className="text-2xl font-bold">{rc.gpa.toFixed(2)}</div><div className="text-xs uppercase text-muted-foreground">GPA</div></div>}
          {rc.attendance_pct != null && <div className="text-center"><div className="text-2xl font-bold">{rc.attendance_pct.toFixed(1)}%</div><div className="text-xs uppercase text-muted-foreground">Attend.</div></div>}
        </div>
        <Table><TableHeader><TableRow><TableHead>Subject</TableHead><TableHead>Components</TableHead><TableHead>Marks</TableHead><TableHead>Grade</TableHead></TableRow></TableHeader>
          <TableBody>
            {rc.payload.subjects.map((s: any) => {
              const cm = s.component_marks || {};
              const rubrics = s.rubrics || [];
              return (
                <TableRow key={s.paper_id}>
                  <TableCell className="font-semibold">{s.subject}</TableCell>
                  <TableCell className="text-xs">
                    {rubrics.length > 0 && !s.is_absent && !s.is_exempted ? (
                      <div className="flex flex-wrap gap-1">
                        {rubrics.map((rb: any) => (
                          <span key={rb.name} className="rounded bg-muted px-1.5 py-0.5">
                            {rb.name}: <span className="font-semibold">{cm[rb.name] ?? "—"}</span>/{rb.max_marks}
                          </span>
                        ))}
                      </div>
                    ) : <span className="text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell>{s.is_absent ? "AB" : s.is_exempted ? "EX" : `${s.marks_obtained} / ${s.total_marks}`}</TableCell>
                  <TableCell><Badge variant="outline">{s.grade}</Badge></TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        {(rc.class_teacher_remark || rc.principal_remark || rc.conduct_remark || rc.discipline_remark) && (
          <div className="space-y-1 rounded-md border p-3 text-sm">
            {rc.conduct_remark && <p><span className="font-semibold">Conduct:</span> {rc.conduct_remark}</p>}
            {rc.discipline_remark && <p><span className="font-semibold">Discipline:</span> {rc.discipline_remark}</p>}
            {rc.class_teacher_remark && <p><span className="font-semibold">Class teacher:</span> {rc.class_teacher_remark}</p>}
            {rc.principal_remark && <p><span className="font-semibold">Principal:</span> {rc.principal_remark}</p>}
          </div>
        )}
        <DialogFooter>
          <DownloadPdfButton rc={rc} />
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


const CLASS_ORDER = ["Nursery", "JKG", "SKG", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12"];

function sortClasses(cs: string[]): string[] {
  return [...cs].sort((a, b) => {
    const ai = CLASS_ORDER.indexOf(a), bi = CLASS_ORDER.indexOf(b);
    if (ai === -1 && bi === -1) return a.localeCompare(b);
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  });
}

function summariseClasses(cs: string[]): string {
  const sorted = sortClasses(cs);
  if (sorted.length === 0) return "—";
  const runs: string[] = [];
  let i = 0;
  while (i < sorted.length) {
    const start = sorted[i];
    const startNum = Number(start);
    if (!Number.isFinite(startNum)) { runs.push(start); i++; continue; }
    let j = i;
    while (j + 1 < sorted.length && Number(sorted[j + 1]) === Number(sorted[j]) + 1) j++;
    runs.push(i === j ? start : `${start}–${sorted[j]}`);
    i = j + 1;
  }
  return runs.join(", ");
}

const MONTH_SHORT = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
function fmtDate(iso: string): string {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  return `${d.getDate()} ${MONTH_SHORT[d.getMonth()]} ${d.getFullYear()}`;
}
function fmtRange(a: string, b: string): string {
  const da = new Date(a + "T00:00:00");
  const db = new Date(b + "T00:00:00");
  if (da.getFullYear() === db.getFullYear() && da.getMonth() === db.getMonth()) {
    return `${da.getDate()}–${db.getDate()} ${MONTH_SHORT[da.getMonth()]} ${da.getFullYear()}`;
  }
  return `${fmtDate(a)} → ${fmtDate(b)}`;
}

function daysUntil(iso: string): number {
  const target = new Date(iso + "T00:00:00").getTime();
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return Math.round((target - now.getTime()) / 86400000);
}

function statusOf(s: ExamSchedule): { label: string; tone: "upcoming" | "active" | "past" } {
  const dStart = daysUntil(s.start_date);
  const dEnd = daysUntil(s.end_date);
  if (dEnd < 0) return { label: "Completed", tone: "past" };
  if (dStart <= 0) return { label: "In progress", tone: "active" };
  if (dStart <= 14) return { label: `In ${dStart} day${dStart === 1 ? "" : "s"}`, tone: "upcoming" };
  return { label: `In ${dStart} days`, tone: "upcoming" };
}

function ExaminationScheduleSection() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "principal";
  const { data: rows = [], isPending } = useQuery({
    queryKey: ["exam-schedules"],
    queryFn: () => examSchedulesService.list(),
  });
  const [editing, setEditing] = useState<ExamSchedule | null>(null);
  const [creating, setCreating] = useState(false);
  const [generating, setGenerating] = useState<ExamSchedule | null>(null);

  const { upcoming, completed } = useMemo(() => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const up: ExamSchedule[] = [];
    const done: ExamSchedule[] = [];
    rows.forEach((r) => {
      const end = new Date(r.end_date + "T00:00:00");
      if (end.getTime() >= today.getTime()) up.push(r);
      else done.push(r);
    });
    return { upcoming: up, completed: done };
  }, [rows]);

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-3">
        <div>
          <CardTitle className="flex items-center gap-2">
            <CalendarDays className="h-5 w-5 text-primary" /> Examination Schedule
          </CardTitle>
          <CardDescription>
            {rows.length} assessment window{rows.length === 1 ? "" : "s"} for the current academic year
          </CardDescription>
        </div>
        {isAdmin && (
          <Button size="sm" variant="outline" onClick={() => setCreating(true)}>
            <Plus className="mr-2 h-4 w-4" /> Add
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        {isPending ? (
          <div className="flex h-24 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
        ) : rows.length === 0 ? (
          <div className="rounded-md border border-dashed p-8 text-center text-sm text-muted-foreground">
            No examinations scheduled yet.
          </div>
        ) : (
          <>
            <div className="grid gap-3 md:grid-cols-2">
              {upcoming.map((r) => (
                <ScheduleRow key={r.id} s={r}
                  onEdit={isAdmin ? () => setEditing(r) : undefined}
                  onGenerate={isAdmin ? () => setGenerating(r) : undefined} />
              ))}
            </div>
            {completed.length > 0 && (
              <details className="mt-2">
                <summary className="cursor-pointer text-sm font-medium text-muted-foreground hover:text-foreground">
                  Show {completed.length} completed exam{completed.length === 1 ? "" : "s"}
                </summary>
                <div className="mt-3 grid gap-3 md:grid-cols-2">
                  {completed.map((r) => (
                    <ScheduleRow key={r.id} s={r}
                      onEdit={isAdmin ? () => setEditing(r) : undefined}
                      onGenerate={isAdmin ? () => setGenerating(r) : undefined} />
                  ))}
                </div>
              </details>
            )}
          </>
        )}
      </CardContent>
      {(editing || creating) && (
        <ExamScheduleDialog
          schedule={editing || undefined}
          onClose={() => { setEditing(null); setCreating(false); }}
        />
      )}
      {generating && (
        <GenerateExamsDialog
          schedule={generating}
          onClose={() => setGenerating(null)}
        />
      )}
    </Card>
  );
}

function ScheduleRow({ s, onEdit, onGenerate }: { s: ExamSchedule; onEdit?: () => void; onGenerate?: () => void }) {
  const st = statusOf(s);
  const tone = st.tone === "active" ? "bg-emerald-500/10 text-emerald-700 border-emerald-500/30"
    : st.tone === "upcoming" ? "bg-blue-500/10 text-blue-700 border-blue-500/30"
    : "bg-slate-500/10 text-slate-500 border-slate-500/30";
  return (
    <div className={`rounded-md border p-3 transition ${st.tone === "past" ? "opacity-70" : "hover:shadow-sm"}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-slate-900 dark:text-slate-100">{s.name}</span>
            <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${tone}`}>
              {st.label}
            </span>
          </div>
          <div className="mt-1 text-sm text-muted-foreground">{fmtRange(s.start_date, s.end_date)}</div>
          <div className="mt-2 flex flex-wrap items-center gap-1 text-xs">
            <span className="text-muted-foreground">Classes:</span>
            <Badge variant="outline">{summariseClasses(s.applies_to)}</Badge>
          </div>
        </div>
        <div className="flex shrink-0 flex-col gap-1">
          {onGenerate && (
            <Button size="icon" variant="ghost" onClick={onGenerate} className="h-8 w-8" title="Generate per-subject exams">
              <Sparkles className="h-3.5 w-3.5 text-primary" />
            </Button>
          )}
          {onEdit && (
            <Button size="icon" variant="ghost" onClick={onEdit} className="h-8 w-8" title="Edit schedule">
              <Pencil className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

const CLASS_CHOICES = ["Nursery","JKG","SKG","1","2","3","4","5","6","7","8","9","10","11","12"];

function ExamScheduleDialog({ schedule, onClose }: { schedule?: ExamSchedule; onClose: () => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState(schedule?.name || "");
  const [start, setStart] = useState(schedule?.start_date || "");
  const [end, setEnd] = useState(schedule?.end_date || "");
  const [classes, setClasses] = useState<string[]>(schedule?.applies_to || []);

  const toggleClass = (c: string) => {
    setClasses((prev) => (prev.includes(c) ? prev.filter((x) => x !== c) : sortClasses([...prev, c])));
  };
  const pickAll = () => setClasses(CLASS_CHOICES);
  const pickNumeric = () => setClasses(CLASS_CHOICES.filter((c) => /^\d+$/.test(c)));

  const save = useMutation({
    mutationFn: () => {
      const payload = { name, start_date: start, end_date: end, applies_to: classes };
      return schedule
        ? examSchedulesService.update(schedule.id, payload)
        : examSchedulesService.create(payload);
    },
    onSuccess: () => {
      toast.success(schedule ? "Schedule updated" : "Schedule added");
      qc.invalidateQueries({ queryKey: ["exam-schedules"] });
      onClose();
    },
    onError: (e: any) => toast.error(e.message || "Save failed"),
  });

  const remove = useMutation({
    mutationFn: () => examSchedulesService.remove(schedule!.id),
    onSuccess: () => {
      toast.info("Schedule removed");
      qc.invalidateQueries({ queryKey: ["exam-schedules"] });
      onClose();
    },
  });

  const canSave = !!name.trim() && !!start && !!end && classes.length > 0 && start <= end;

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{schedule ? "Edit examination" : "Add examination"}</DialogTitle>
          <DialogDescription>Sets a school-wide assessment window for the selected classes.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Summative Assessment-I" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Start date</Label>
              <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">End date</Label>
              <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} />
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Applies to classes</Label>
              <div className="flex gap-1 text-[11px]">
                <button type="button" className="text-primary underline" onClick={pickAll}>All</button>
                <span className="text-muted-foreground">·</span>
                <button type="button" className="text-primary underline" onClick={pickNumeric}>1–12</button>
                <span className="text-muted-foreground">·</span>
                <button type="button" className="text-primary underline" onClick={() => setClasses([])}>Clear</button>
              </div>
            </div>
            <div className="mt-2 flex flex-wrap gap-1">
              {CLASS_CHOICES.map((c) => (
                <button key={c} type="button" onClick={() => toggleClass(c)}
                  className={`rounded-full border px-3 py-1 text-xs font-medium transition
                    ${classes.includes(c) ? "border-primary bg-primary text-primary-foreground" : "border-border bg-background hover:bg-muted"}`}>
                  {c}
                </button>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          {schedule && (
            <Button variant="outline" onClick={() => { if (confirm("Delete this schedule?")) remove.mutate(); }} disabled={remove.isPending}>
              <Trash2 className="mr-2 h-4 w-4 text-destructive" /> Delete
            </Button>
          )}
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => save.mutate()} disabled={!canSave || save.isPending}>
            {save.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function GenerateExamsDialog({ schedule, onClose }: { schedule: ExamSchedule; onClose: () => void }) {
  const qc = useQueryClient();
  const [subjectsText, setSubjectsText] = useState("");   // comma-sep override
  const [totalMarks, setTotalMarks] = useState<string>(""); // "" = use default
  const [overwrite, setOverwrite] = useState(false);

  const subjectsParam = subjectsText.trim() ? subjectsText.trim() : undefined;
  const marksParam = totalMarks ? Number(totalMarks) : undefined;

  const preview = useQuery({
    queryKey: ["exam-schedules", schedule.id, "preview", subjectsParam, marksParam],
    queryFn: () => examSchedulesService.generatePreview(schedule.id, {
      subjects: subjectsParam, total_marks: marksParam,
    }),
  });

  const generate = useMutation({
    mutationFn: () => examSchedulesService.generate(schedule.id, {
      subjects: subjectsParam ? subjectsParam.split(",").map((s) => s.trim()).filter(Boolean) : undefined,
      total_marks: marksParam,
      overwrite,
    }),
    onSuccess: (r) => {
      const parts = [`${r.created} created`];
      if (r.updated > 0) parts.push(`${r.updated} updated`);
      if (r.skipped > 0) parts.push(`${r.skipped} skipped`);
      toast.success(`Exams generated — ${parts.join(", ")}`);
      qc.invalidateQueries({ queryKey: ["exams"] });
      qc.invalidateQueries({ queryKey: ["exam-schedules"] });
      onClose();
    },
    onError: (e: any) => toast.error(e.message || "Generation failed"),
  });

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" /> Auto-generate exams for {schedule.name}
          </DialogTitle>
          <DialogDescription>
            Creates per-subject <span className="font-mono">Exam</span> rows for every class in this schedule.
            Subjects default to a sensible list per class-level; dates are distributed evenly across the window.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">
              Subjects (optional — comma-separated)
            </Label>
            <Input value={subjectsText}
              placeholder="Leave empty for class-level defaults (e.g. English, Hindi, Mathematics…)"
              onChange={(e) => setSubjectsText(e.target.value)} />
            <p className="mt-1 text-[11px] text-muted-foreground">
              Overrides the default for ALL classes. Leave blank to use the built-in per-bucket lists (pre-primary / primary / middle / secondary / senior).
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Total marks (optional)</Label>
              <Input type="number" value={totalMarks} onChange={(e) => setTotalMarks(e.target.value)}
                placeholder="Auto (40 / 80)" />
            </div>
            <label className="flex items-start gap-2 rounded-md border border-border p-3 text-sm">
              <input type="checkbox" className="mt-1" checked={overwrite}
                onChange={(e) => setOverwrite(e.target.checked)} />
              <span>
                <span className="font-medium">Update matching exams</span>
                <span className="block text-[11px] text-muted-foreground">
                  If exam rows with the same name/class/subject already exist, refresh their date and total marks.
                </span>
              </span>
            </label>
          </div>

          {/* Preview */}
          <div className="rounded-md border border-primary/30 bg-primary/5 p-3">
            <div className="mb-2 flex items-center justify-between">
              <div className="text-sm font-semibold">Preview</div>
              <div className="text-lg font-bold text-primary tabular-nums">
                {preview.isFetching ? <Loader2 className="h-4 w-4 animate-spin" /> : `${preview.data?.total_exams ?? 0} exams`}
              </div>
            </div>
            {preview.data && (
              <div className="max-h-[280px] overflow-y-auto rounded-md border bg-background">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-muted/70">
                    <tr className="text-left">
                      <th className="p-2">Class</th>
                      <th className="p-2">Subjects · dates</th>
                      <th className="p-2 text-right">Marks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.data.plans.map((pl) => (
                      <tr key={pl.class_name} className="border-t border-border align-top">
                        <td className="p-2 font-semibold">Class {pl.class_name}</td>
                        <td className="p-2">
                          <div className="flex flex-wrap gap-1">
                            {pl.subjects.map((sub, i) => (
                              <span key={sub} className="rounded bg-muted px-2 py-0.5">
                                {sub} <span className="text-muted-foreground">· {pl.dates[i]}</span>
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="p-2 text-right tabular-nums">{pl.total_marks}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => generate.mutate()} disabled={generate.isPending || !preview.data?.total_exams}>
            {generate.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
            Generate {preview.data?.total_exams ?? 0} exams
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

